import { Injectable, inject } from '@angular/core';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { EcommerceEvent, EcommerceEventParameters } from './analytics.models';
import { GA4EventNameEnum, GA4ParameterNameEnum, UserTypeEnum } from './analytics.constants';
import { safeErrorSummary } from '../../helper/safe-error-summary';

/**
 * Enhanced Analytics Service
 * @description Centralized service for Google Analytics 4 tracking
 * Wraps the existing GoogleAnalyticsService with additional features:
 * - Type-safe event tracking
 * - Automatic parameter enrichment
 * - Error handling and fallback mechanisms
 * - Development mode logging
 */
@Injectable({
  providedIn: 'root',
})
export class AnalyticsService {
  private readonly gaService = inject(GoogleAnalyticsService);
  private isDevelopmentMode = true;
  private currentUserId: string | null = null;
  private currentUserType: string = UserTypeEnum.ANONYMOUS;

  constructor() {
    // Initialize with development mode check
    this.isDevelopmentMode = !this.isProduction();
  }

  /**
   * Check if running in production mode
   * @private
   */
  private isProduction(): boolean {
    try {
      // Check if window is defined (SSR safety)
      if (typeof window === 'undefined') {
        return false;
      }
      // This will be set based on environment configuration
      return false; // Default to false for development
    } catch (error) {
      this.logError('Error checking production mode', error);
      return false;
    }
  }

  /**
   * Set user ID for tracking
   * @param userId - User ID from Auth0 or null to clear
   */
  public setUserId(userId: string | null): void {
    try {
      this.currentUserId = userId;

      if (typeof window !== 'undefined' && (window as any).gtag) {
        (window as any).gtag('set', 'user_id', userId);
      }

      this.logDebug(`User ID set: ${userId || 'cleared'}`);
    } catch (error) {
      this.logError('Error setting user ID', error);
    }
  }

  /**
   * Set user type for automatic enrichment
   * @param userType - Type of user (admin, operator, customer, anonymous)
   */
  public setUserType(userType: string): void {
    try {
      this.currentUserType = userType;
      this.logDebug(`User type set: ${userType}`);
    } catch (error) {
      this.logError('Error setting user type', error);
    }
  }

  /**
   * Track page view with enhanced parameters
   * @param pagePath - Path of the page being viewed
   * @param pageTitle - Title of the page
   * @param customParameters - Additional custom parameters
   */
  public trackPageView(
    pagePath: string,
    pageTitle: string,
    customParameters?: Record<string, string | number | boolean>,
  ): void {
    try {
      // Enrich parameters with automatic data
      const enrichedParams: Record<string, string | number | boolean> = {
        ...customParameters,
        [GA4ParameterNameEnum.USER_TYPE]: customParameters?.[GA4ParameterNameEnum.USER_TYPE] || this.currentUserType,
        [GA4ParameterNameEnum.TIMESTAMP]: new Date().toISOString(),
      };

      // Get page location for the location parameter
      let pageLocation = '';
      if (enrichedParams[GA4ParameterNameEnum.PAGE_LOCATION]) {
        pageLocation = String(enrichedParams[GA4ParameterNameEnum.PAGE_LOCATION]);
        // Remove from options since it goes in location parameter
        delete enrichedParams[GA4ParameterNameEnum.PAGE_LOCATION];
      } else if (typeof window !== 'undefined') {
        pageLocation = window.location.href;
      }

      this.logDebug('Page view:', { pagePath, pageTitle, location: pageLocation, parameters: enrichedParams });

      // Call underlying GA service with correct signature
      // pageView(path: string, title?: string, location?: string, options?: Object)
      this.gaService.pageView(pagePath, pageTitle, pageLocation, enrichedParams);
    } catch (error) {
      this.logError('Error tracking page view', error);
      this.fallbackTracking('pageview', { pagePath, pageTitle });
    }
  }

  /**
   * Track custom event
   * @param eventName - Name of the event
   * @param parameters - Event parameters
   */
  public trackEvent(eventName: string, parameters?: Record<string, string | number | boolean>): void {
    try {
      // Enrich parameters with automatic data
      const enrichedParams: Record<string, string | number | boolean> = {
        ...parameters,
        [GA4ParameterNameEnum.USER_TYPE]: parameters?.[GA4ParameterNameEnum.USER_TYPE] || this.currentUserType,
        [GA4ParameterNameEnum.TIMESTAMP]: new Date().toISOString(),
      };

      this.logDebug('Event:', { eventName, parameters: enrichedParams });

      // Call underlying GA service
      // event(action: string, category?: string, label?: string, value?: number, interaction?: boolean, options?: Object)
      // Pass enrichedParams as the options parameter
      this.gaService.event(eventName, undefined, undefined, undefined, undefined, enrichedParams);
    } catch (error) {
      this.logError('Error tracking event', error);
      this.fallbackTracking('event', { eventName, parameters });
    }
  }

  /**
   * Track e-commerce event
   * @param event - E-commerce event details
   */
  public trackEcommerceEvent(event: EcommerceEvent): void {
    try {
      const enrichedParams: EcommerceEventParameters = {
        ...event.parameters,
        [GA4ParameterNameEnum.USER_TYPE]: event.parameters[GA4ParameterNameEnum.USER_TYPE] || this.currentUserType,
        [GA4ParameterNameEnum.TIMESTAMP]: new Date().toISOString(),
      };

      this.logDebug('E-commerce event:', { eventName: event.eventName, parameters: enrichedParams });

      // Call underlying GA service
      // event(action: string, category?: string, label?: string, value?: number, interaction?: boolean, options?: Object)
      this.gaService.event(event.eventName, undefined, undefined, undefined, undefined, enrichedParams);
    } catch (error) {
      this.logError('Error tracking e-commerce event', error);
      this.fallbackTracking('ecommerce', event);
    }
  }

  /**
   * Track button click
   * @param buttonName - Name/ID of the button clicked
   * @param additionalParams - Additional parameters
   */
  public trackButtonClick(buttonName: string, additionalParams?: Record<string, string | number | boolean>): void {
    this.trackEvent(GA4EventNameEnum.BUTTON_CLICK, {
      event_label: buttonName,
      ...additionalParams,
    });
  }

  /**
   * Track form submission
   * @param formName - Name/ID of the form
   * @param additionalParams - Additional parameters
   */
  public trackFormSubmit(formName: string, additionalParams?: Record<string, string | number | boolean>): void {
    this.trackEvent(GA4EventNameEnum.FORM_SUBMIT, {
      event_label: formName,
      ...additionalParams,
    });
  }

  /**
   * Fallback tracking mechanism if GA fails
   * @private
   */
  private fallbackTracking(type: string, data: unknown): void {
    try {
      if (this.isDevelopmentMode) {
        console.warn('[Analytics Fallback]', type, data);
      }

      // Could implement alternative tracking here (e.g., send to custom endpoint)
    } catch (error) {
      // Silent fail for fallback
    }
  }

  /**
   * Log debug message in development mode
   * @private
   */
  private logDebug(message: string, data?: unknown): void {
    if (this.isDevelopmentMode) {
      if (data) {
        console.warn(`[Analytics Debug] ${message}`, data);
      } else {
        console.warn(`[Analytics Debug] ${message}`);
      }
    }
  }

  /**
   * Log error message
   * @private
   */
  private logError(message: string, error: unknown): void {
    if (this.isDevelopmentMode) {
      console.error(`[Analytics Error] ${message}`, safeErrorSummary(error));
    }
  }

  /**
   * Get current user ID
   */
  public getUserId(): string | null {
    return this.currentUserId;
  }

  /**
   * Get current user type
   */
  public getUserType(): string {
    return this.currentUserType;
  }
}
